/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useEffect, useMemo, useReducer, useState } from 'react';
import {
  WebSocketClientMessageTypes,
  webSocketClientAllMessages,
} from './messageTypes';
import {
  addSubscriptionAction,
  initialState,
  onMessageReceivedAction,
  reducer,
  removeSubscriptionAction,
} from './reducer';
import { Socket, io } from 'socket.io-client';
import config from 'config/environment';

export type SubscriptionId = string;

export interface IWebSocketClientContext {
  subscribe: (
    messageType: WebSocketClientMessageTypes,
    callback: (message: any) => void
  ) => SubscriptionId;
  unsubscribe: (subscriptionId: SubscriptionId) => void;
}

export const WebSocketClientContext = createContext<IWebSocketClientContext>(
  {} as IWebSocketClientContext
);

export interface WebSocketClientWrapperProps {
  sessionToken?: string;
  children: React.ReactChild;
}

export function WebSocketClientWrapper(props: WebSocketClientWrapperProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const webSocketClientContextValue = useMemo(() => {
    function subscribe(
      messageType: WebSocketClientMessageTypes,
      callback: (message: any) => void
    ) {
      const subscriptionId = `${messageType}-${Date.now()}`;

      const subscription = {
        id: subscriptionId,
        webSocketClientMessageType: messageType,
        callback,
      };

      dispatch(addSubscriptionAction(subscription));
      return subscriptionId;
    }

    function unsubscribe(subscriptionId: SubscriptionId) {
      dispatch(removeSubscriptionAction(subscriptionId));
    }

    return {
      subscribe,
      unsubscribe,
    } as IWebSocketClientContext;
  }, []);

  useEffect(() => {
    if (!props.sessionToken) {
      return;
    }

    const ws = io(config.WS_URL, {
      transports: ['websocket'],
      auth: {
        token: props.sessionToken,
      },
    });

    ws.on('connect', () => {
      console.log('### Socket connected');
    });

    webSocketClientAllMessages.forEach((messageType) => {
      ws.on(messageType, (data) => {
        console.log(`### Received message (${messageType})`);
        dispatch(onMessageReceivedAction(messageType, data));
      });
    });

    ws.on('error', (error) => {
      console.error('### Socket error:', error);
    });

    ws.on('disconnect', () => {
      console.log('### Socket disconnected');
    });

    return () => {
      if (ws) {
        console.log('### Disconnecting ws...');
        ws.disconnect();
      }
    };
  }, [props.sessionToken]);

  return (
    <WebSocketClientContext.Provider value={webSocketClientContextValue}>
      {props.children}
    </WebSocketClientContext.Provider>
  );
}
